import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';
import { Network } from '@capacitor/network';
import { logInfo } from './logger';

// Explains why a device went offline (#1572 follow-up): app sent to background, JS thread frozen
// (Doze / battery optimisation), or network lost while the app stayed awake.
// sendLog does not buffer, so events seen while offline are kept here and reported on reconnect.
const TICK_MS = 5000;
const FREEZE_MS = 15000; // a 5s timer firing 15s+ late means the OS suspended the JS thread
const MAX_EVENTS = 30;

const events: string[] = [];
let started = false;
let isActive = true;
let lastTick = 0;
let lostAt: number | null = null;
let atLoss: Record<string, unknown> = {};
let appInfo: Record<string, unknown> = {};

// Ring buffer of timestamped events, flushed with the next Device State Report
const record = (event: string) => {
  events.push(`${new Date().toISOString().slice(11, 19)} ${event}`);
  if (events.length > MAX_EVENTS) events.shift();
};

const appState = () => ({ isActive, visibility: document.visibilityState });

export const startPowerDiagnostics = (): void => {
  if (started || !['ios', 'android'].includes(Capacitor.getPlatform())) return; // isNativePlatform() is also true on Electron (capacitor-community/electron)
  started = true;
  lastTick = Date.now(); // start from here, not module load: start may run minutes later (after login) and fake a freeze
  App.getInfo().then(({ version, build }) => { appInfo = { appVersion: version, appBuild: build }; }).catch(() => {});
  App.addListener('appStateChange', ({ isActive: active }) => {
    isActive = active;
    record(active ? 'foreground' : 'background');
    logInfo('App State Change', appState());
  });
  Network.addListener('networkStatusChange', ({ connected, connectionType }) => record(`network ${connected ? connectionType : 'none'}`));
  setInterval(() => {
    const now = Date.now();
    const gapMs = now - lastTick;
    lastTick = now;
    if (gapMs < FREEZE_MS) return;
    record(`frozen ${Math.round(gapMs / 1000)}s ${isActive ? 'foreground' : 'background'}`);
    logInfo('JS Freeze Detected', { gapMs, ...appState() });
  }, TICK_MS);
};

// sinceTickMs above FREEZE_MS: the drop was only noticed when the JS thread woke up
export const markConnectionLost = (reason?: string): void => {
  if (!started || lostAt) return;
  lostAt = Date.now();
  atLoss = { ...appState(), sinceTickMs: lostAt - lastTick };
  record(`signalr lost ${reason ?? ''}`.trim());
};

// Reports device state plus buffered offline events once SignalR is (re)connected
export const reportDeviceState = async (trigger: string): Promise<void> => {
  if (!started) return;
  const [battery, network] = await Promise.all([
    Device.getBatteryInfo().catch(() => undefined),
    Network.getStatus().catch(() => undefined),
  ]);
  logInfo('Device State Report', { trigger, offlineMs: lostAt ? Date.now() - lostAt : null, atLoss, now: appState(), isCharging: battery?.isCharging, batteryLevel: battery?.batteryLevel, connectionType: network?.connectionType, ...appInfo, events: events.splice(0) });
  lostAt = null;
  atLoss = {};
};
